import type { CalculatedDimension, CalculatedMeasure, Expression } from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import type { AIDiagnostic } from '../errors.js';
import { DEFAULT_AI_CORE_LIMITS, type AICoreLimits } from '../limits.js';
import { findActionReferenceIssues } from '../validate-ai-response.js';

/** Hijos directos de un nodo del Expression AST. */
function children(expression: Expression): Expression[] {
  switch (expression.type) {
    case 'literal':
    case 'column':
    case 'measure':
      return [];
    case 'unary':
      return [expression.operand];
    case 'binary':
      return [expression.left, expression.right];
    case 'function':
      return expression.args;
    case 'aggregate':
      return [expression.argument, expression.filter].filter(
        (child): child is Expression => child !== undefined,
      );
    case 'case':
      return [
        ...expression.branches.flatMap((branch) => [branch.when, branch.then]),
        ...(expression.else === undefined ? [] : [expression.else]),
      ];
  }
}

/** Profundidad y numero de nodos de un AST (iterativo: no desborda la pila). */
export function measureExpression(expression: Expression): { depth: number; nodes: number } {
  let depth = 0;
  let nodes = 0;
  const stack: { node: Expression; level: number }[] = [{ node: expression, level: 1 }];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) break;
    nodes += 1;
    depth = Math.max(depth, current.level);
    for (const child of children(current.node))
      stack.push({ node: child, level: current.level + 1 });
  }
  return { depth, nodes };
}

function limitIssues(expression: Expression, limits: AICoreLimits): string[] {
  const { depth, nodes } = measureExpression(expression);
  const issues: string[] = [];
  if (depth > limits.maxExpressionDepth) {
    issues.push(
      `La expresion supera la profundidad maxima (${String(limits.maxExpressionDepth)}).`,
    );
  }
  if (nodes > limits.maxExpressionNodes) {
    issues.push(`La expresion supera el maximo de nodos (${String(limits.maxExpressionNodes)}).`);
  }
  return issues;
}

const toDiagnostics = (messages: readonly string[], actionId: string | undefined): AIDiagnostic[] =>
  messages.map((message) => ({
    stage: 'semantic',
    code: 'SEMANTIC_ERROR',
    path: 'payload',
    message,
    ...(actionId === undefined ? {} : { actionId }),
  }));

/**
 * Valida una medida calculada propuesta por la IA sobre un modelo candidato: solo AST
 * (nunca SQL), referencias existentes, tipos, aridad de funciones, agregacion compatible,
 * ausencia de ciclos e ids unicos, ademas de limites de tamano del AST.
 *
 * @example
 * validateCalculatedMeasure({ id: 'margin_pct', name: 'Margen %', expression }, index); // []
 */
export function validateCalculatedMeasure(
  measure: CalculatedMeasure,
  index: SemanticModelIndex,
  limits: AICoreLimits = DEFAULT_AI_CORE_LIMITS,
  actionId?: string,
): AIDiagnostic[] {
  return toDiagnostics(
    [
      ...limitIssues(measure.expression, limits),
      ...findActionReferenceIssues(
        { id: actionId ?? 'calc', type: 'createMeasure', payload: { measure } },
        index,
      ),
    ],
    actionId,
  );
}

/** Igual que `validateCalculatedMeasure` para dimensiones calculadas (fila a fila, sin agregaciones). */
export function validateCalculatedDimension(
  dimension: CalculatedDimension,
  index: SemanticModelIndex,
  limits: AICoreLimits = DEFAULT_AI_CORE_LIMITS,
  actionId?: string,
): AIDiagnostic[] {
  return toDiagnostics(
    [
      ...limitIssues(dimension.expression, limits),
      ...findActionReferenceIssues(
        { id: actionId ?? 'calc', type: 'createDimension', payload: { dimension } },
        index,
      ),
    ],
    actionId,
  );
}
